import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  MessageSquare, Mail, X, AlertCircle, Smartphone, Check, SkipForward,
  ChevronLeft, RotateCcw, Phone, QrCode, FileText, Loader2, Copy,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  buildSmsLink, buildMailtoLink, canSendNativeSms, copyToClipboard, detectPlatform, isStandaloneApp,
} from '../../../lib/smsLink';
import { fetchTemplates } from '../../communications/api/communicationsApi';

/**
 * Bulk "communicator" that sends from the AGENT'S OWN number / address.
 *
 * Unlike the Twilio / Brevo business send, this hands each personalised message
 * off to the phone's native Messages app (``sms:``) or the device's mail app
 * (``mailto:``) via a deep link, so the message goes out from the agent's
 * personal number / address and replies land with them. Because each body is
 * personalised, the composer is opened one recipient at a time — that's the
 * "one at a time" stepper.
 *
 * SMS works on iOS (Apple Messages) and Android (Google/Samsung Messages); the
 * ``sms:`` URI is built per-platform, and on desktop (no SIM) it shows a QR so
 * the agent can reopen the page on their phone. Email uses ``mailto:``, which
 * desktop mail clients honour too, so the Email channel is not gated to phones.
 *
 * The Email channel only appears when an ``emailHandlers`` prop is supplied
 * (the per-property Enquiries/Buyers tabs), so the inspection Bulk Communicator
 * stays SMS-only. Both channels share the same hand-off/tracking machinery.
 *
 * The hand-off is a real <a href> the agent taps — a scripted ``location.href``
 * is silently dropped when the CRM runs as an iPhone home-screen app, which made
 * the whole queue look "sent" while nothing opened. If the hand-off still
 * doesn't switch apps we show a copy-the-text fallback, and the "Sent"
 * confirmation only unlocks once a hand-off was actually attempted, so the CRM
 * stops logging messages that never left the device. Progress is kept in
 * localStorage because iOS restarts home-screen apps when you come back.
 *
 * Decoupled from how many inspections are involved via two callbacks per channel:
 *   loadMessages({ attendeeIds, template })                → { messages, skipped }  (SMS)
 *   loadMessages({ attendeeIds, subject, template })       → { messages, skipped }  (email)
 *   markHandedOff(attendeeIds) → void
 */

export const DEFAULT_DEVICE_SMS_TEMPLATE =
  "Hi {first_name}, thanks for visiting {address}.\n\n" +
  "Download the contract, view comparable sales and access exclusive " +
  "off-market properties through Collings:\n\n" +
  "{link}";

export const DEFAULT_DEVICE_EMAIL_SUBJECT = "Following up on {address}";

export const DEFAULT_DEVICE_EMAIL_TEMPLATE =
  "Hi {first_name},\n\n" +
  "Thanks for your interest in {address}. You can download the contract, view " +
  "comparable sales and access exclusive off-market properties through Collings " +
  "here:\n\n{link}\n\n{agent_name}";

const TOKENS = ['{first_name}', '{agent_name}', '{address}', '{suburb}', '{link}', '{next_oh}'];

// Unsent progress is remembered this long, so an agent who gets pulled away (or
// whose home-screen app is restarted by iOS) can pick the queue back up.
const RESUME_TTL_MS = 3 * 60 * 60 * 1000;
const RESUME_PREFIX = 'bulkComm:v1:';

/**
 * Convert a saved Communications-Hub SMS template (which uses {{DoubleBrace}}
 * merge tags aimed at a contact list) into the Bulk Communicator's {single_brace}
 * device tokens, so a template written for campaigns can be reused here.
 * Tags with no device equivalent (State, Company, Email) are dropped — the
 * backend tidies the leftover spacing so nothing shows as a gap.
 */
const HUB_TAG_MAP = {
  firstname: '{first_name}',
  first_name: '{first_name}',
  name: '{first_name}',
  suburb: '{suburb}',
};

export function hubTemplateToDeviceTemplate(smsBody) {
  return (smsBody || '')
    .replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (_m, tag) => HUB_TAG_MAP[tag.toLowerCase()] || '')
    .trim();
}

export default function BulkCommunicatorModal({
  attendees = [],
  loadMessages,
  markHandedOff,
  // Optional Email channel. When supplied, a Text/Email toggle appears and the
  // agent can hand each email to their own mail app. Same shape as the SMS
  // handlers, but loadMessages also receives a { subject }.
  emailHandlers = null,
  onClose,
  // Distinguishes queues that share a URL (Attendees vs Enquiries vs Buyers on
  // the same property) so resuming can't mix them up.
  scopeKey = '',
}) {
  const platform = detectPlatform();
  const nativeCapable = canSendNativeSms();
  const standalone = isStandaloneApp();

  const emailEnabled = !!(emailHandlers && emailHandlers.loadMessages);

  const [channel, setChannel] = useState('sms'); // 'sms' | 'email'
  const isEmail = channel === 'email';

  // mailto: works on desktop; sms: needs a SIM. Only SMS falls back to the QR.
  const channelCapable = isEmail ? true : nativeCapable;

  const activeHandlers = isEmail
    ? emailHandlers
    : { loadMessages, markHandedOff };

  const storageKey = useMemo(() => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    return `${RESUME_PREFIX}${path}|${scopeKey}|${channel}`;
  }, [scopeKey, channel]);

  // Only recipients reachable on the active channel (a phone for SMS, an email
  // address for Email) can be messaged.
  const reachable = useMemo(
    () => attendees.filter((a) => (isEmail ? (a.email || '').trim() : (a.phone || '').trim())),
    [attendees, isEmail],
  );

  const [step, setStep] = useState('compose'); // compose | send | desktop
  const [template, setTemplate] = useState(DEFAULT_DEVICE_SMS_TEMPLATE);
  const [emailSubject, setEmailSubject] = useState(DEFAULT_DEVICE_EMAIL_SUBJECT);
  const [emailBody, setEmailBody] = useState(DEFAULT_DEVICE_EMAIL_TEMPLATE);
  const [selected, setSelected] = useState(() => reachable.map((a) => a.id));
  const [loading, setLoading] = useState(false);

  // Switching channel changes who is reachable and which queue is saved, so
  // reset the selection to everyone reachable on the new channel and step back
  // to compose. Kept off the effect deps (reads a ref) so a re-render that hands
  // in a fresh `attendees` array doesn't wipe the agent's manual selection.
  const attendeesRef = useRef(attendees);
  useEffect(() => { attendeesRef.current = attendees; });
  const prevChannelRef = useRef(channel);
  useEffect(() => {
    if (prevChannelRef.current === channel) return;
    prevChannelRef.current = channel;
    const list = attendeesRef.current.filter(
      (a) => (channel === 'email' ? (a.email || '').trim() : (a.phone || '').trim()),
    );
    setSelected(list.map((a) => a.id));
    setStep('compose');
  }, [channel]);

  // Saved templates (from the Communications Hub Templates tab), lazily loaded
  // the first time the agent opens the picker. SMS channel only — the Hub's
  // email templates are HTML, which a plain-text mailto: body can't carry.
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState(null); // null = not loaded yet
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const openTemplatePicker = async () => {
    setShowTemplates((v) => !v);
    if (templates !== null || templatesLoading) return;
    setTemplatesLoading(true);
    try {
      const data = await fetchTemplates({ channel: 'sms' });
      const list = Array.isArray(data) ? data : (data?.results || []);
      setTemplates(list.filter((t) => (t.sms_body || '').trim()));
    } catch (err) {
      setTemplates([]);
      toast.error('Could not load saved templates.');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const applyTemplate = (tpl) => {
    setTemplate(hubTemplateToDeviceTemplate(tpl.sms_body));
    setShowTemplates(false);
    toast.success(`Loaded "${tpl.name}".`);
  };

  const resetTemplate = () => {
    if (isEmail) {
      setEmailSubject(DEFAULT_DEVICE_EMAIL_SUBJECT);
      setEmailBody(DEFAULT_DEVICE_EMAIL_TEMPLATE);
    } else {
      setTemplate(DEFAULT_DEVICE_SMS_TEMPLATE);
    }
  };

  const insertToken = (t) => {
    if (isEmail) {
      setEmailBody((prev) => `${prev}${prev.endsWith(' ') || !prev ? '' : ' '}${t}`);
    } else {
      setTemplate((prev) => `${prev}${prev.endsWith(' ') || !prev ? '' : ' '}${t}`);
    }
  };

  // Queue state (step === 'send')
  const [queue, setQueue] = useState([]);     // [{ attendee_id, name, phone|email, subject?, body }]
  const [skipped, setSkipped] = useState([]); // [{ id, name, reason }]
  const [index, setIndex] = useState(0);
  const [sentIds, setSentIds] = useState([]);
  const [finishing, setFinishing] = useState(false);

  // Hand-off tracking for the current recipient.
  const [handedOff, setHandedOff] = useState(false);   // hand-off attempted
  const [handoffFailed, setHandoffFailed] = useState(false); // never left the app
  const [copied, setCopied] = useState('');
  const failTimer = useRef(null);

  const clearFailTimer = useCallback(() => {
    if (failTimer.current) {
      clearTimeout(failTimer.current);
      failTimer.current = null;
    }
  }, []);
  useEffect(() => clearFailTimer, [clearFailTimer]);

  // ---- Resume an interrupted queue -------------------------------------------
  const [savedProgress, setSavedProgress] = useState(null);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) { setSavedProgress(null); return; }
      const data = JSON.parse(raw);
      const fresh = Date.now() - (data?.savedAt || 0) < RESUME_TTL_MS;
      const unfinished = Array.isArray(data?.queue) && data.index < data.queue.length;
      if (fresh && unfinished) setSavedProgress(data);
      else { window.localStorage.removeItem(storageKey); setSavedProgress(null); }
    } catch (err) {
      /* a corrupt/blocked store just means no resume offer */
    }
  }, [storageKey]);

  const clearSaved = useCallback(() => {
    try { window.localStorage.removeItem(storageKey); } catch (err) { /* ignore */ }
    setSavedProgress(null);
  }, [storageKey]);

  // Persist after every step so an app restart (iOS kills home-screen apps when
  // you switch away) doesn't lose a half-worked queue.
  useEffect(() => {
    if (step !== 'send' || !queue.length) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({
        savedAt: Date.now(), channel, template, emailSubject, emailBody, queue, skipped, index, sentIds,
      }));
    } catch (err) { /* storage full/blocked — resume just won't be offered */ }
  }, [step, queue, skipped, index, sentIds, template, emailSubject, emailBody, channel, storageKey]);

  const resumeSaved = () => {
    if (!savedProgress) return;
    setTemplate(savedProgress.template || DEFAULT_DEVICE_SMS_TEMPLATE);
    setEmailSubject(savedProgress.emailSubject || DEFAULT_DEVICE_EMAIL_SUBJECT);
    setEmailBody(savedProgress.emailBody || DEFAULT_DEVICE_EMAIL_TEMPLATE);
    setQueue(savedProgress.queue || []);
    setSkipped(savedProgress.skipped || []);
    setSentIds(savedProgress.sentIds || []);
    setIndex(savedProgress.index || 0);
    setHandedOff(false);
    setHandoffFailed(false);
    setSavedProgress(null);
    setStep('send');
  };

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const allSelected = reachable.length > 0 && selected.length === reachable.length;
  const toggleAll = () => setSelected(allSelected ? [] : reachable.map((a) => a.id));

  const handleContinue = async () => {
    if (!selected.length) return;
    if (!channelCapable) { setStep('desktop'); return; }
    setLoading(true);
    try {
      const args = isEmail
        ? { attendeeIds: selected, subject: emailSubject, template: emailBody }
        : { attendeeIds: selected, template };
      const { messages, skipped: skp } = await activeHandlers.loadMessages(args);
      if (!messages?.length) {
        const optedOut = (skp || []).filter((s) => s.reason === 'opted_out').length;
        const missing = (skp || []).filter(
          (s) => s.reason === 'no_phone' || s.reason === 'no_email',
        ).length;
        const parts = [];
        if (missing) parts.push(`${missing} with no ${isEmail ? 'email' : 'number'}`);
        if (optedOut) parts.push(`${optedOut} opted out`);
        toast.error(
          parts.length
            ? `Nothing to send: ${parts.join(', ')}.`
            : 'Nothing to send for the selected buyers.',
        );
        setSkipped(skp || []);
        setLoading(false);
        return;
      }
      setQueue(messages);
      setSkipped(skp || []);
      setIndex(0);
      setSentIds([]);
      setHandedOff(false);
      setHandoffFailed(false);
      setStep('send');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not prepare messages.');
    } finally {
      setLoading(false);
    }
  };

  const current = queue[index];
  const handoffHref = current
    ? (isEmail
        ? buildMailtoLink(current.email, current.subject, current.body)
        : buildSmsLink(current.phone, current.body, platform))
    : '';

  const appName = isEmail ? 'your mail app' : 'Messages';

  // The "Open …" <a href> below does the actual hand-off (a scripted navigation
  // is swallowed by home-screen/in-app web views). This only records the attempt
  // and watches whether we really left the app — if we're still visible a moment
  // later the deep link was blocked, so offer the copy route.
  const handleHandoff = () => {
    setHandedOff(true);
    setHandoffFailed(false);
    clearFailTimer();
    failTimer.current = setTimeout(() => {
      failTimer.current = null;
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        setHandoffFailed(true);
      }
    }, 2500);
  };

  // Losing the screen (or focus, e.g. the "open in …?" sheet) means the hand-off
  // worked — cancel the pending failure notice.
  useEffect(() => {
    const handedOver = () => {
      clearFailTimer();
      setHandoffFailed(false);
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') handedOver(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', handedOver);
    window.addEventListener('pagehide', handedOver);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', handedOver);
      window.removeEventListener('pagehide', handedOver);
    };
  }, [clearFailTimer]);

  const copyValue = async (label, value) => {
    const ok = await copyToClipboard(value);
    if (!ok) { toast.error('Could not copy — select the text and copy it manually.'); return; }
    setCopied(label);
    setHandedOff(true); // copying is a valid way to send it by hand
    setTimeout(() => setCopied(''), 1800);
  };

  const advance = (didSend) => {
    clearFailTimer();
    setHandedOff(false);
    setHandoffFailed(false);
    if (didSend && current) setSentIds((prev) => [...prev, current.attendee_id]);
    const next = index + 1;
    if (next < queue.length) {
      setIndex(next);
    } else {
      finish(didSend && current ? [...sentIds, current.attendee_id] : sentIds);
    }
  };

  const finish = async (ids) => {
    setFinishing(true);
    const noun = isEmail ? 'email' : 'message';
    try {
      if (ids.length) await activeHandlers.markHandedOff(ids);
      clearSaved();
      toast.success(
        ids.length
          ? `${ids.length} ${noun}${ids.length === 1 ? '' : 's'} sent from your ${isEmail ? 'mail app' : 'phone'}.`
          : 'Nothing marked as sent.',
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sent, but could not update the CRM log.');
    } finally {
      setFinishing(false);
      onClose?.();
    }
  };

  // ----- Desktop fallback: QR to reopen on the agent's phone (SMS only) -----
  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';

  const ChannelIcon = isEmail ? Mail : MessageSquare;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col my-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <ChannelIcon className="w-4 h-4 text-[#4EADB7]" />
            <div>
              <h2 className="text-base font-semibold text-gray-900">Bulk Communicator</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {isEmail
                  ? "Email buyers from your own address via your mail app."
                  : "Text buyers from your own number via your phone's Messages app."}
              </p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="-mr-1 p-1 text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
        </div>

        {/* Channel toggle — mirrors the Communications composer's SMS/Email tabs. */}
        {emailEnabled && (step === 'compose') && (
          <div className="flex-shrink-0 px-5 pt-4">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
              {['sms', 'email'].map((ch) => (
                <button
                  key={ch}
                  type="button"
                  onClick={() => setChannel(ch)}
                  className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-bold ${
                    channel === ch ? 'bg-white text-[#4EADB7] shadow-sm' : 'text-gray-500'
                  }`}
                >
                  {ch === 'sms' ? <MessageSquare className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                  {ch === 'sms' ? 'Text' : 'Email'}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ---------------- COMPOSE ---------------- */}
        {step === 'compose' && (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {/* Unfinished queue from earlier (or before iOS restarted the app) */}
              {savedProgress && (
                <div className="border border-[#4EADB7]/40 bg-[#4EADB7]/5 rounded-lg px-3 py-3">
                  <p className="text-xs text-slate-700">
                    You were part-way through {isEmail ? 'emailing' : 'texting'}{' '}
                    <strong>{savedProgress.queue.length}</strong> buyer
                    {savedProgress.queue.length === 1 ? '' : 's'} —{' '}
                    {savedProgress.index} done, {savedProgress.queue.length - savedProgress.index} to go.
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <button type="button" onClick={resumeSaved}
                      className="text-xs px-3 py-1.5 bg-[#4EADB7] hover:bg-[#3f9ba4] text-white rounded-md">
                      Resume
                    </button>
                    <button type="button" onClick={clearSaved}
                      className="text-xs px-3 py-1.5 border rounded-md bg-white hover:bg-slate-50 text-slate-600">
                      Start over
                    </button>
                  </div>
                </div>
              )}

              {/* Template */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">
                    {isEmail ? 'Email' : 'Message'} template
                  </label>
                  <div className="flex items-center gap-3">
                    {!isEmail && (
                      <button type="button" onClick={openTemplatePicker}
                        className="text-xs text-[#4EADB7] hover:underline flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Templates
                      </button>
                    )}
                    <button type="button" onClick={resetTemplate}
                      className="text-xs text-[#4EADB7] hover:underline flex items-center gap-1">
                      <RotateCcw className="w-3 h-3" /> Reset
                    </button>
                  </div>
                </div>

                {/* Saved SMS templates picker (from the Communications Hub) */}
                {!isEmail && showTemplates && (
                  <div className="mb-2 border rounded-lg bg-slate-50 divide-y max-h-48 overflow-y-auto">
                    {templatesLoading && (
                      <div className="flex items-center gap-2 px-3 py-3 text-xs text-slate-500">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading templates…
                      </div>
                    )}
                    {!templatesLoading && templates && templates.length === 0 && (
                      <div className="px-3 py-3 text-xs text-slate-500">
                        No saved SMS templates yet. Create them under Communications → Templates.
                      </div>
                    )}
                    {!templatesLoading && (templates || []).map((tpl) => (
                      <button key={tpl.id} type="button" onClick={() => applyTemplate(tpl)}
                        className="w-full text-left px-3 py-2 hover:bg-white transition-colors">
                        <div className="text-xs font-medium text-slate-800 truncate">{tpl.name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{tpl.sms_body}</div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Email subject */}
                {isEmail && (
                  <input
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Subject"
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-[#4EADB7]"
                  />
                )}

                <textarea
                  value={isEmail ? emailBody : template}
                  onChange={(e) => (isEmail ? setEmailBody(e.target.value) : setTemplate(e.target.value))}
                  rows={isEmail ? 6 : 4}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#4EADB7] resize-none"
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] text-slate-400 mr-0.5">Insert:</span>
                  {TOKENS.map((t) => (
                    <button key={t} type="button"
                      onClick={() => insertToken(t)}
                      className="text-[11px] font-mono px-2.5 py-1.5 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 active:bg-slate-300 transition-colors">
                      {t}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Each buyer's name, the address, suburb, your name and a tracked link are filled in automatically.
                </p>
              </div>

              {/* Recipient selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">
                    Recipients ({reachable.length} with a{isEmail ? 'n email' : ' number'})
                  </span>
                  {reachable.length > 0 && (
                    <button type="button" onClick={toggleAll} className="text-xs text-[#4EADB7] hover:underline">
                      {allSelected ? 'Clear all' : 'Select all'}
                    </button>
                  )}
                </div>
                <div className="max-h-56 overflow-y-auto divide-y border rounded-lg">
                  {attendees.length === 0 && (
                    <p className="text-sm text-slate-500 py-6 text-center">No attendees yet.</p>
                  )}
                  {attendees.map((a) => {
                    const contact = (isEmail ? a.email : a.phone || '') || '';
                    const value = String(contact).trim();
                    const disabled = !value;
                    const checked = selected.includes(a.id);
                    return (
                      <label key={a.id}
                        className={`flex items-center gap-3 px-3 py-2.5 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}>
                        <input type="checkbox" className="accent-[#4EADB7]" checked={checked} disabled={disabled}
                          onChange={() => !disabled && toggle(a.id)} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{a.name || a.full_name || 'Unnamed buyer'}</div>
                          <div className="text-[11px] text-slate-500 flex items-center gap-1 truncate">
                            {value ? (
                              <>{isEmail ? <Mail className="w-3 h-3 shrink-0" /> : <Phone className="w-3 h-3 shrink-0" />} {value}</>
                            ) : (
                              <span className="flex items-center gap-1 text-amber-600">
                                <AlertCircle className="w-3 h-3 shrink-0" /> No {isEmail ? 'email' : 'phone'} on file
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {!channelCapable && (
                <div className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <Smartphone className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  You're on a desktop, which can't text from your number. Continue to get a QR code to reopen this on your phone.
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-5 py-4 border-t bg-slate-50 rounded-b-2xl">
              <span className="text-xs text-slate-500">{selected.length} selected</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={onClose}
                  className="text-xs px-3 py-2 border rounded-md bg-white hover:bg-slate-50 text-slate-700">Cancel</button>
                <button type="button" onClick={handleContinue} disabled={loading || !selected.length}
                  className="text-xs flex items-center gap-1 px-3 py-2 bg-[#4EADB7] hover:bg-[#3f9ba4] disabled:opacity-50 text-white rounded-md">
                  <ChannelIcon className="w-3.5 h-3.5" />
                  {loading ? 'Preparing…' : channelCapable ? `Continue (${selected.length})` : 'Get QR for phone'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ---------------- SEND (stepper) ---------------- */}
        {step === 'send' && current && (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => setStep('compose')}
                  className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                  <ChevronLeft className="w-3.5 h-3.5" /> Edit
                </button>
                <span className="text-xs font-medium text-slate-500">Buyer {index + 1} of {queue.length}</span>
              </div>

              {/* Progress bar */}
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#4EADB7] transition-all" style={{ width: `${(index / queue.length) * 100}%` }} />
              </div>

              <div className="bg-slate-50 border rounded-xl p-4">
                <div className="text-sm font-semibold text-slate-900">{current.name || 'Buyer'}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1 mb-3">
                  {isEmail ? <Mail className="w-3 h-3" /> : <Phone className="w-3 h-3" />} {isEmail ? current.email : current.phone}
                </div>
                {isEmail && current.subject && (
                  <div className="text-xs font-semibold text-slate-700 mb-1 bg-white border rounded-lg px-3 py-2">
                    {current.subject}
                  </div>
                )}
                <div className="text-sm text-slate-700 whitespace-pre-wrap bg-white border rounded-lg p-3">{current.body}</div>
              </div>

              {/* A real link, not a scripted navigation — home-screen apps and
                  in-app browsers ignore the latter and nothing would open. */}
              <a href={handoffHref} onClick={handleHandoff}
                className="w-full flex items-center justify-center gap-2 bg-[#4EADB7] hover:bg-[#3f9ba4] text-white font-semibold rounded-lg py-3 transition-colors">
                {isEmail ? <Mail className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
                {handedOff ? `Open ${appName} again` : `Open ${appName}`}
              </a>
              <p className="text-[11px] text-slate-400 text-center">
                Tap <strong>Send</strong> in {isEmail ? 'your mail app' : 'Messages'}, then come back and mark it below.
              </p>

              {/* Deep link blocked (typical of the home-screen app): let the agent
                  copy the text and send it by hand. */}
              {handoffFailed && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 space-y-2">
                  <p className="flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
                    <span>
                      Didn't {appName} open? {standalone && !isEmail
                        ? 'Apps added to your home screen can block this — open the CRM in Safari or Chrome, or copy the text below and send it by hand.'
                        : `Copy the text below and send it from ${isEmail ? 'your mail app' : 'Messages'}, or try again.`}
                    </span>
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={() => copyValue('body', current.body)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white border border-amber-300 hover:bg-amber-100">
                      <Copy className="w-3 h-3" /> {copied === 'body' ? 'Copied' : 'Copy message'}
                    </button>
                    <button type="button" onClick={() => copyValue('to', isEmail ? current.email : current.phone)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white border border-amber-300 hover:bg-amber-100">
                      <Copy className="w-3 h-3" /> {copied === 'to' ? 'Copied' : (isEmail ? 'Copy email' : 'Copy number')}
                    </button>
                    {standalone && !isEmail && (
                      // In a home-screen app this opens Safari, where the sms:
                      // hand-off works — the agent restarts the send there.
                      <a href={pageUrl} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white border border-amber-300 hover:bg-amber-100">
                        Open in browser
                      </a>
                    )}
                  </div>
                </div>
              )}

              {skipped.length > 0 && index === 0 && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {skipped.length} buyer{skipped.length === 1 ? '' : 's'} skipped (no {isEmail ? 'email' : 'number'} or opted out).
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-5 py-4 border-t bg-slate-50 rounded-b-2xl">
              <button type="button" onClick={() => advance(false)} disabled={finishing}
                className="text-xs flex items-center gap-1 px-3 py-2 border rounded-md bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50">
                <SkipForward className="w-3.5 h-3.5" /> Skip
              </button>
              <div className="flex flex-col items-end gap-1">
                {/* Only confirmable once the hand-off happened (or the text was
                    copied), so the CRM can't log messages that never went out. */}
                <button type="button" onClick={() => advance(true)} disabled={finishing || !handedOff}
                  className="text-xs flex items-center gap-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md disabled:opacity-50">
                  <Check className="w-3.5 h-3.5" />
                  {index + 1 < queue.length ? 'Sent — Next' : (finishing ? 'Finishing…' : 'Sent — Finish')}
                </button>
                {!handedOff && (
                  <span className="text-[10px] text-slate-400">Open {appName} first</span>
                )}
              </div>
            </div>
          </>
        )}

        {/* ---------------- DESKTOP fallback (SMS only) ---------------- */}
        {step === 'desktop' && (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-6 flex flex-col items-center text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-[#4EADB7]/10 flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-[#4EADB7]" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Open this on your phone</h3>
                <p className="text-xs text-slate-500 mt-1 max-w-xs">
                  To text buyers from your own number, scan this with your phone to reopen this property, then tap Bulk Communicator there.
                </p>
              </div>
              {pageUrl ? (
                <div className="bg-white p-4 rounded-lg border">
                  <QRCodeCanvas value={pageUrl} size={200} level="M" includeMargin={false} />
                </div>
              ) : (
                <div className="w-52 h-52 flex items-center justify-center bg-white border rounded-lg"><QrCode className="w-10 h-10 text-gray-300" /></div>
              )}
              <p className="text-[11px] text-slate-400 break-all max-w-xs">{pageUrl}</p>
            </div>
            <div className="flex items-center justify-between gap-2 px-5 py-4 border-t bg-slate-50 rounded-b-2xl">
              <button type="button" onClick={() => setStep('compose')}
                className="text-xs flex items-center gap-1 px-3 py-2 border rounded-md bg-white hover:bg-slate-50 text-slate-700">
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
              <button type="button" onClick={onClose}
                className="text-xs px-3 py-2 bg-[#4EADB7] hover:bg-[#3f9ba4] text-white rounded-md">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
